import React, { useEffect, useState, useCallback } from "react";
import { ShieldCheck, KeyRound, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SuperAdminCredentialsCard — admin self-service to rotate the super-admin
 * login id + password from the UI (no env redeploy).
 *
 * Visible only when the active session role is "admin" (env-based super admin
 * or DB-overridden one). The component itself returns null otherwise.
 */
const SuperAdminCredentialsCard = ({ authAxios, role }) => {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/super-admin/profile`);
      setProfile(r.data);
      setNewUsername(r.data?.username || "");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load super-admin profile");
    }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { if (role === "admin") fetchProfile(); }, [role, fetchProfile]);

  if (role !== "admin") return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setOk("");
    if (!currentPassword || !newUsername || !newPassword) { setErr("All fields are required"); return; }
    if (newPassword !== confirmPassword) { setErr("New password and confirm do not match"); return; }
    setSaving(true);
    try {
      const r = await authAxios.post(`${API_URL}/api/admin/super-admin/update`, {
        current_password: currentPassword,
        new_username: newUsername.trim(),
        new_password: newPassword,
      });
      setOk(`Updated. Active super-admin login id is now "${r.data?.username || newUsername}".`);
      // Reflect changes immediately + clear secrets from memory
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
      // Persist the fresh JWT for refresh-survival (mirrors Admin.js bootstrap)
      if (r.data?.token) {
        try { sessionStorage.setItem("doozygo_admin_jwt", r.data.token); } catch { /* private mode */ }
        // Update cached metadata username so the sidebar greeting refreshes after reload
        try {
          const raw = localStorage.getItem("doozygo_admin");
          if (raw) {
            const meta = JSON.parse(raw);
            meta.username = r.data?.username || newUsername;
            localStorage.setItem("doozygo_admin", JSON.stringify(meta));
          }
        } catch { /* ignore */ }
      }
      fetchProfile();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Update failed");
    }
    setSaving(false);
  };

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-2xl p-5 shadow-sm" data-testid="super-admin-credentials-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-600 text-white">
          <ShieldCheck size={18} />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-bold text-[#1E1B4B] font-['Outfit']">Super Admin Credentials</h3>
          <p className="text-xs text-[#64748B] mt-0.5">
            Rotate the super-admin login id and password. The change takes effect immediately —
            new sessions must use the new credentials. The original .env values remain a recovery path
            until a DB override is set.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="py-6 flex justify-center"><Loader2 size={20} className="animate-spin text-indigo-500" /></div>
      ) : (
        <>
          <div className="bg-indigo-50 border border-indigo-100 rounded-xl px-3 py-2 mb-4 text-xs text-indigo-800 flex items-center gap-2">
            <KeyRound size={14} />
            <span>
              Active login id: <b data-testid="super-admin-active-username">{profile?.username || "—"}</b>
              {profile?.is_overridden ? (
                <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px] font-semibold">
                  <CheckCircle2 size={10} /> DB override
                </span>
              ) : (
                <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold">
                  <AlertTriangle size={10} /> using .env defaults
                </span>
              )}
            </span>
          </div>

          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-[#64748B] block mb-1">Current password</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Verify with the password you logged in with"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:border-indigo-400 outline-none"
                data-testid="super-admin-current-password"
                autoComplete="current-password"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#64748B] block mb-1">New login id</label>
              <input
                type="text"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                placeholder="e.g. founder@doozygo"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:border-indigo-400 outline-none"
                data-testid="super-admin-new-username"
                autoComplete="username"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#64748B] block mb-1">New password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="≥ 8 chars · upper · lower · number · special"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:border-indigo-400 outline-none"
                data-testid="super-admin-new-password"
                autoComplete="new-password"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-[#64748B] block mb-1">Confirm new password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:border-indigo-400 outline-none"
                data-testid="super-admin-confirm-password"
                autoComplete="new-password"
              />
            </div>

            {err && (
              <div className="sm:col-span-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-xs text-red-700 flex items-center gap-2" data-testid="super-admin-update-error">
                <AlertTriangle size={14} /> {err}
              </div>
            )}
            {ok && (
              <div className="sm:col-span-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2 text-xs text-emerald-700 flex items-center gap-2" data-testid="super-admin-update-success">
                <CheckCircle2 size={14} /> {ok}
              </div>
            )}

            <div className="sm:col-span-2 flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-fuchsia-600 text-white text-sm font-semibold disabled:opacity-50 flex items-center gap-2"
                data-testid="super-admin-save-btn"
              >
                {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : "Save credentials"}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default SuperAdminCredentialsCard;
